import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, X, FolderPlus } from "lucide-react";
import { ChefIcon, CheckIcon, CameraIcon, VideoIcon, ClapperboardIcon, DownloadIcon, SpoonIcon, NoteIcon, EyeIcon, FireIcon } from './Icons';

export default function AddRecipeDialog({
  open, onOpenChange,
  // State
  extractionMode, setExtractionMode,
  videoUrl, setVideoUrl,
  extracting, extractionStep, extractionProgress, isPreparingExtraction,
  recipeImageFiles, recipeImagePreviews, recipeImageInput,
  recipeSource, setRecipeSource,
  categories,
  selectedCategoryForRecipe, setSelectedCategoryForRecipe,
  showInlineNewCategory, setShowInlineNewCategory,
  inlineNewCategoryName, setInlineNewCategoryName,
  creatingInlineCategory,
  manualRecipe, setManualRecipe,
  savingManualRecipe,
  // Handlers
  checkVideoDurationAndExtract,
  handleExtractFromImage,
  handleImageUploadForRecipe,
  handleCreateInlineCategory,
  handleSaveManualRecipe,
  addIngredientField, removeIngredientField, updateIngredient,
  addInstructionField, removeInstructionField, updateInstruction,
  removeImage
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-recipe-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter une recette</DialogTitle>
        </DialogHeader>
        <div className="add-recipe-form">
          {/* Onglets pour choisir le mode d'extraction */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid #F5F1E8' }}>
            <button
              onClick={() => setExtractionMode('video')}
              data-testid="tab-video"
              style={{
                flex: 1, padding: '0.75rem',
                background: extractionMode === 'video' ? '#87A96B' : 'transparent',
                color: extractionMode === 'video' ? 'white' : '#8B7355',
                border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
                fontWeight: extractionMode === 'video' ? 'bold' : 'normal',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                fontSize: '0.85rem', transition: 'all 0.2s'
              }}
            >
              <VideoIcon size={18} /> Lien vidéo
            </button>
            <button
              onClick={() => setExtractionMode('image')}
              data-testid="tab-image"
              style={{
                flex: 1, padding: '0.75rem',
                background: extractionMode === 'image' ? '#87A96B' : 'transparent',
                color: extractionMode === 'image' ? 'white' : '#8B7355',
                border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
                fontWeight: extractionMode === 'image' ? 'bold' : 'normal',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                fontSize: '0.85rem', transition: 'all 0.2s'
              }}
            >
              <CameraIcon size={18} /> Photo
            </button>
            <button
              onClick={() => setExtractionMode('manual')}
              data-testid="tab-manual"
              style={{
                flex: 1, padding: '0.75rem',
                background: extractionMode === 'manual' ? '#87A96B' : 'transparent',
                color: extractionMode === 'manual' ? 'white' : '#8B7355',
                border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
                fontWeight: extractionMode === 'manual' ? 'bold' : 'normal',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                fontSize: '0.85rem', transition: 'all 0.2s'
              }}
            >
              <ChefIcon size={18} /> Manuel
            </button>
          </div>

          {/* Mode Vidéo */}
          {extractionMode === 'video' && (
            <div>
              <Label htmlFor="videoUrl">Collez le lien de la vidéo</Label>
              <Input
                id="videoUrl"
                data-testid="video-url-input"
                placeholder="https://www.tiktok.com/... ou YouTube, Instagram, Facebook"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                disabled={extracting}
                style={{ marginBottom: '0.75rem' }}
              />
              <div style={{ fontSize: '0.75rem', color: '#8B7355', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <ClapperboardIcon size={14} />
                <span>TikTok, YouTube Shorts, Instagram Reels, Facebook</span>
              </div>

              {/* Catégorie */}
              <CategorySelector
                categories={categories}
                selectedCategoryForRecipe={selectedCategoryForRecipe}
                setSelectedCategoryForRecipe={setSelectedCategoryForRecipe}
                showInlineNewCategory={showInlineNewCategory}
                setShowInlineNewCategory={setShowInlineNewCategory}
                inlineNewCategoryName={inlineNewCategoryName}
                setInlineNewCategoryName={setInlineNewCategoryName}
                creatingInlineCategory={creatingInlineCategory}
                handleCreateInlineCategory={handleCreateInlineCategory}
              />

              <Button
                onClick={checkVideoDurationAndExtract}
                disabled={!videoUrl || extracting || !selectedCategoryForRecipe}
                data-testid="extract-video-btn"
                style={{ width: '100%', background: '#87A96B', color: 'white' }}
              >
                {isPreparingExtraction ? (
                  <><Loader2 className="animate-spin" size={16} style={{ marginRight: '0.5rem' }} /> Vérification...</>
                ) : extracting ? (
                  <><Loader2 className="animate-spin" size={16} style={{ marginRight: '0.5rem' }} /> {extractionStep || 'Extraction...'}</>
                ) : (
                  <><DownloadIcon size={16} style={{ marginRight: '0.5rem' }} /> Extraire la recette</>
                )}
              </Button>

              {extracting && (
                <ExtractionProgress extractionStep={extractionStep} extractionProgress={extractionProgress} />
              )}
            </div>
          )}

          {/* Mode Image */}
          {extractionMode === 'image' && (
            <div>
              <Label>Ajoutez des photos de recette</Label>
              <p style={{ fontSize: '0.8rem', color: '#8B7355', marginBottom: '0.75rem' }}>
                Analyse de photos de recettes, pages de magazine, captures d'écran... L'IA extraira les ingrédients et instructions.
              </p>

              <input
                type="file"
                accept="image/*,.heic,.heif"
                multiple
                onChange={handleImageUploadForRecipe}
                ref={recipeImageInput}
                style={{ display: 'none' }}
              />
              
              <div 
                onClick={() => recipeImageInput.current?.click()}
                data-testid="image-upload-zone"
                style={{
                  border: '2px dashed #D4A574', borderRadius: '12px', padding: '1.5rem',
                  textAlign: 'center', cursor: 'pointer', marginBottom: '1rem',
                  background: '#FFF9E6', transition: 'all 0.2s'
                }}
              >
                <CameraIcon size={32} style={{ marginBottom: '0.5rem', color: '#D4A574' }} />
                <p style={{ color: '#8B7355', fontWeight: '600', margin: '0.25rem 0' }}>Cliquez pour ajouter des photos</p>
                <p style={{ color: '#8B7355', fontSize: '0.75rem' }}>JPG, PNG, HEIC - Max 5 images, 10 MB chaque</p>
              </div>

              {/* Aperçu des images */}
              {recipeImagePreviews.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '0.5rem', marginBottom: '1rem' }}>
                  {recipeImagePreviews.map((preview, index) => (
                    <div key={`preview-${index}`} style={{ position: 'relative' }}>
                      <img src={preview} alt={`Aperçu ${index + 1}`} 
                        style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #E8E4D9' }} />
                      <button onClick={() => removeImage(index)}
                        style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#DC3545', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Source de la recette */}
              <div style={{ marginBottom: '0.75rem' }}>
                <Label htmlFor="recipeSource">Source (optionnel)</Label>
                <Input id="recipeSource" placeholder="Nom du livre, magazine, site web..." value={recipeSource} onChange={(e) => setRecipeSource(e.target.value)} />
              </div>

              {/* Catégorie */}
              <CategorySelector
                categories={categories}
                selectedCategoryForRecipe={selectedCategoryForRecipe}
                setSelectedCategoryForRecipe={setSelectedCategoryForRecipe}
                showInlineNewCategory={showInlineNewCategory}
                setShowInlineNewCategory={setShowInlineNewCategory}
                inlineNewCategoryName={inlineNewCategoryName}
                setInlineNewCategoryName={setInlineNewCategoryName}
                creatingInlineCategory={creatingInlineCategory}
                handleCreateInlineCategory={handleCreateInlineCategory}
              />

              <Button onClick={handleExtractFromImage}
                disabled={recipeImageFiles.length === 0 || extracting || !selectedCategoryForRecipe}
                data-testid="extract-image-btn"
                style={{ width: '100%', background: '#87A96B', color: 'white' }}>
                {extracting ? (
                  <><Loader2 className="animate-spin" size={16} style={{ marginRight: '0.5rem' }} /> {extractionStep || 'Analyse...'}</>
                ) : (
                  <><EyeIcon size={16} style={{ marginRight: '0.5rem' }} /> Extraire la recette ({recipeImageFiles.length} photo{recipeImageFiles.length > 1 ? 's' : ''})</>
                )}
              </Button>

              {extracting && <ExtractionProgress extractionStep={extractionStep} extractionProgress={extractionProgress} />}
            </div>
          )}

          {/* Mode Manuel */}
          {extractionMode === 'manual' && (
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <Label htmlFor="manualTitle">Nom de la recette *</Label>
                <Input id="manualTitle" data-testid="manual-title-input" placeholder="Ex: Poutine classique"
                  value={manualRecipe.title} onChange={(e) => setManualRecipe({...manualRecipe, title: e.target.value})} />
              </div>

              <CategorySelector
                categories={categories}
                selectedCategoryForRecipe={selectedCategoryForRecipe}
                setSelectedCategoryForRecipe={setSelectedCategoryForRecipe}
                showInlineNewCategory={showInlineNewCategory}
                setShowInlineNewCategory={setShowInlineNewCategory}
                inlineNewCategoryName={inlineNewCategoryName}
                setInlineNewCategoryName={setInlineNewCategoryName}
                creatingInlineCategory={creatingInlineCategory}
                handleCreateInlineCategory={handleCreateInlineCategory}
              />

              {/* Infos */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <div>
                  <Label>Préparation</Label>
                  <Input placeholder="15 min" value={manualRecipe.prepTime} onChange={(e) => setManualRecipe({...manualRecipe, prepTime: e.target.value})} />
                </div>
                <div>
                  <Label>Cuisson</Label>
                  <Input placeholder="30 min" value={manualRecipe.cookTime} onChange={(e) => setManualRecipe({...manualRecipe, cookTime: e.target.value})} />
                </div>
                <div>
                  <Label>Portions</Label>
                  <Input placeholder="4" value={manualRecipe.servings} onChange={(e) => setManualRecipe({...manualRecipe, servings: e.target.value})} />
                </div>
              </div>

              {/* Ingrédients */}
              <div style={{ marginBottom: '0.75rem' }}>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <SpoonIcon size={16} /> Ingrédients *
                </Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {manualRecipe.ingredients.map((ingredient, index) => (
                    <div key={`ingredient-${index}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <Input placeholder="Ex: 2 tasses de farine" value={ingredient}
                        onChange={(e) => updateIngredient(index, e.target.value)} style={{ flex: 1 }} />
                      {manualRecipe.ingredients.length > 1 && (
                        <button onClick={() => removeIngredientField(index)}
                          style={{ background: '#FFE8E8', border: '1px solid #DC3545', borderRadius: '8px', padding: '0.5rem', cursor: 'pointer' }}>
                          <X size={14} color="#DC3545" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={addIngredientField} data-testid="add-ingredient-btn"
                    style={{ padding: '0.5rem', background: '#F5F1E8', border: '1px dashed #D4A574', borderRadius: '8px', cursor: 'pointer', color: '#8B7355', fontSize: '0.85rem' }}>
                    + Ajouter un ingrédient
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div style={{ marginBottom: '0.75rem' }}>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <NoteIcon size={16} /> Instructions *
                </Label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {manualRecipe.instructions.map((instruction, index) => (
                    <div key={`instruction-${index}`} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <span style={{ background: '#D4A574', color: 'white', borderRadius: '50%', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold', flexShrink: 0, marginTop: '0.5rem' }}>
                        {index + 1}
                      </span>
                      <Input placeholder={`Étape ${index + 1}...`} value={instruction}
                        onChange={(e) => updateInstruction(index, e.target.value)} style={{ flex: 1 }} />
                      {manualRecipe.instructions.length > 1 && (
                        <button onClick={() => removeInstructionField(index)}
                          style={{ background: '#FFE8E8', border: '1px solid #DC3545', borderRadius: '8px', padding: '0.5rem', cursor: 'pointer', marginTop: '0.25rem' }}>
                          <X size={14} color="#DC3545" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={addInstructionField} data-testid="add-instruction-btn"
                    style={{ padding: '0.5rem', background: '#F5F1E8', border: '1px dashed #D4A574', borderRadius: '8px', cursor: 'pointer', color: '#8B7355', fontSize: '0.85rem' }}>
                    + Ajouter une étape
                  </button>
                </div>
              </div>

              <Button onClick={handleSaveManualRecipe}
                disabled={!manualRecipe.title || manualRecipe.ingredients.filter(i => i.trim()).length === 0 || manualRecipe.instructions.filter(i => i.trim()).length === 0 || !selectedCategoryForRecipe || savingManualRecipe}
                data-testid="save-manual-recipe-btn"
                style={{ width: '100%', background: '#87A96B', color: 'white' }}>
                {savingManualRecipe ? (
                  <><Loader2 className="animate-spin" size={16} style={{ marginRight: '0.5rem' }} /> Création...</>
                ) : (
                  <><CheckIcon size={16} style={{ marginRight: '0.5rem' }} /> Créer la recette</>
                )}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Sous-composant: Sélecteur de catégorie (réutilisé 3 fois)
function CategorySelector({
  categories, selectedCategoryForRecipe, setSelectedCategoryForRecipe,
  showInlineNewCategory, setShowInlineNewCategory,
  inlineNewCategoryName, setInlineNewCategoryName,
  creatingInlineCategory, handleCreateInlineCategory
}) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <Label>Catégorie *</Label>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Select value={selectedCategoryForRecipe} onValueChange={setSelectedCategoryForRecipe}>
          <SelectTrigger data-testid="category-select" style={{ flex: 1 }}>
            <SelectValue placeholder="Choisir une catégorie" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setShowInlineNewCategory(!showInlineNewCategory)} variant="outline"
          style={{ padding: '0.5rem', minWidth: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FolderPlus size={18} />
        </Button>
      </div>
      {showInlineNewCategory && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <Input placeholder="Nouvelle catégorie..." value={inlineNewCategoryName}
            onChange={(e) => setInlineNewCategoryName(e.target.value)} autoFocus style={{ flex: 1 }} />
          <Button onClick={handleCreateInlineCategory} disabled={!inlineNewCategoryName.trim() || creatingInlineCategory}
            style={{ background: '#87A96B', color: 'white' }}>
            {creatingInlineCategory ? <Loader2 className="animate-spin" size={14} /> : <CheckIcon size={14} />}
          </Button>
        </div>
      )}
    </div>
  );
}


// Sous-composant: Barre de progression d'extraction ANIMÉE
function ExtractionProgress({ extractionStep, extractionProgress = 0 }) {
  const stepStr = typeof extractionStep === 'string' ? extractionStep : String(extractionStep || '');
  
  const steps = [
    { key: 'download', label: 'Téléchargement', icon: <DownloadIcon size={16} />, threshold: 10 },
    { key: 'transcri', label: 'Lecture audio', icon: <FireIcon size={16} />, threshold: 30 },
    { key: 'extraction', label: 'Extraction IA', icon: <ChefIcon size={16} />, threshold: 50 },
    { key: 'analyse', label: 'Analyse', icon: <SpoonIcon size={16} />, threshold: 75 },
    { key: 'sauvegarde', label: 'Finalisation', icon: <CheckIcon size={16} />, threshold: 85 },
  ];

  // Utiliser le vrai progress du backend (jamais en arrière grâce au Math.max dans Dashboard)
  const progress = Math.max(extractionProgress, 2);

  // Messages fun rotatifs
  const funMessages = [
    'On attrape la vidéo...', 'Capture en cours...', 
    'On écoute le chef...', 'Analyse des saveurs...',
    'Notre chef IA cuisine...', 'Décortiquage des ingrédients...',
    'On mesure en tasses...', 'Conversion québécoise...',
    'Dernière touche...', 'On dresse l\'assiette!'
  ];
  
  const [funMsgIndex, setFunMsgIndex] = React.useState(0);
  React.useEffect(() => {
    const interval = setInterval(() => setFunMsgIndex(prev => prev + 1), 3500);
    return () => clearInterval(interval);
  }, []);

  const foodEmojis = ['🍳', '🥘', '🍲', '🥗', '🍰', '🧁', '🍕', '🌮', '🥐', '🍜'];
  const currentFood = foodEmojis[funMsgIndex % foodEmojis.length];
  const displayMessage = stepStr || funMessages[funMsgIndex % funMessages.length];

  return (
    <div data-testid="extraction-progress" style={{
      marginTop: '1rem', padding: '1.25rem', 
      background: 'linear-gradient(135deg, #F5F1E8 0%, #FFF9E6 100%)',
      borderRadius: '16px', border: '2px solid #E8E4D9',
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Header avec message et pourcentage */}
      <div style={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '0.5rem'
      }}>
        <span style={{ 
          fontSize: '0.9rem', fontWeight: '700', color: '#5D6F61',
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          <span style={{ 
            fontSize: '1.4rem',
            animation: 'bounce 1s ease infinite',
            display: 'inline-block'
          }}>{currentFood}</span>
          {displayMessage}
        </span>
        <span style={{ 
          fontSize: '0.8rem', fontWeight: '700', color: '#87A96B',
          background: '#E8F5E2', padding: '0.2rem 0.6rem', borderRadius: '999px'
        }}>
          {Math.round(progress)}%
        </span>
      </div>
      
      {/* Barre de progression */}
      <div style={{
        width: '100%', height: '10px', background: '#E8E4D9',
        borderRadius: '999px', overflow: 'hidden', position: 'relative'
      }}>
        <div style={{
          height: '100%',
          width: `${progress}%`,
          background: 'linear-gradient(90deg, #87A96B, #6B8F50, #87A96B)',
          backgroundSize: '200% 100%',
          borderRadius: '999px',
          transition: 'width 1.5s ease-out',
          animation: 'shimmer 2s ease-in-out infinite'
        }} />
      </div>

      {/* Étapes avec indicateurs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '1rem' }}>
        {steps.map((step, index) => {
          const isCompleted = progress >= step.threshold + 5;
          const isActive = !isCompleted && progress >= (index === 0 ? 0 : steps[index - 1].threshold);
          
          return (
            <div key={step.key} style={{ 
              display: 'flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.35rem 0.5rem', borderRadius: '8px',
              background: isActive ? 'rgba(135, 169, 107, 0.15)' : 'transparent',
              transition: 'all 0.4s ease'
            }}>
              <div style={{
                width: '22px', height: '22px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isCompleted ? '#87A96B' : isActive ? '#FFF' : '#E8E4D9',
                border: isActive ? '2px solid #87A96B' : '2px solid transparent',
                transition: 'all 0.3s ease',
                flexShrink: 0
              }}>
                {isCompleted ? (
                  <CheckIcon size={12} style={{ color: 'white' }} />
                ) : isActive ? (
                  <Loader2 className="animate-spin" size={12} style={{ color: '#87A96B' }} />
                ) : (
                  <span style={{ fontSize: '0.6rem', color: '#B0A690' }}>{index + 1}</span>
                )}
              </div>
              <span style={{ 
                color: isActive ? '#5D6F61' : isCompleted ? '#87A96B' : '#B0A690',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                transition: 'all 0.3s ease'
              }}>
                {step.icon}
                <span style={{ 
                  fontSize: '0.8rem', 
                  fontWeight: isActive ? '700' : isCompleted ? '600' : '400',
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  opacity: isCompleted ? 0.7 : 1
                }}>
                  {step.label}
                </span>
              </span>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}
